import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from "@angular/core";
import { MAT_CHECKBOX_DEFAULT_OPTIONS, MatCheckboxDefaultOptions } from "@angular/material/checkbox";
import { Observable, Subject, map, take, takeUntil, tap } from "rxjs";

import { BreakpointObserver } from "@angular/cdk/layout";
import { MatDialog } from "@angular/material/dialog";
import { MatSlideToggleChange } from "@angular/material/slide-toggle";
import { CellularDevice } from "@app/cellular/models/cellular-device.model";
import { CellularService } from "@app/cellular/services/cellular.service";
import { ClareServicePlan } from "@app/project/model/clare-service-plan";
import { ClareServiceSubscription } from "@app/project/model/clare-service-subscription";
import { Project } from "@app/project/model/project";
import { AdditionalFeature, UIAdditionalFeature } from "@app/project/model/project-feature";
import { SERVICE_PLAN_DIALOG_VIEW } from "@app/project/model/service-plan";
import { ServicePlanFeature } from "@app/project/model/service-plan-feature";
import { ProjectStatusService } from "@app/project/services/project-status.service";
import { ProjectService } from "@app/project/services/project.service";
import { ServicePlanUtils } from "@app/project/services/service-plan-utils";
import { ServicePlansService } from "@app/project/services/service-plans.service";
import { ClareStatuses } from "@app/shared/model/clare-status.model";
import { NgChanges } from "@app/shared/model/ng-changes.model";

@Component({
    selector: "app-service-plan-options",
    templateUrl: "./service-plan-options.component.html",
    styleUrls: ["./service-plan-options.component.scss"],
    providers: [
        {
            provide: MAT_CHECKBOX_DEFAULT_OPTIONS,
            useValue: { clickAction: "noop" } as MatCheckboxDefaultOptions,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServicePlanOptionsComponent implements OnChanges, OnInit, OnDestroy {
    @ViewChild("dialogContent") dialogContent: ElementRef<HTMLDivElement>;
    @Input() currentPlan: ClareServicePlan;
    @Input() selectedPlan: ClareServicePlan;
    @Input() subscription: ClareServiceSubscription;
    @Input() project: Project;
    @Input() cellularDevice: CellularDevice;
    @Input() activeView = SERVICE_PLAN_DIALOG_VIEW.SELECT_PLAN;
    @Input() isInWizard = false;
    @Input() isCompareBtnHidden = false;
    @Input() isSelectionDisabled = false;
    @Input() isBackOnSelectIgnore = false;

    @Output() onUpdateDone = new EventEmitter();
    @Output() fieldsChange = new EventEmitter();

    plans$: Observable<ClareServicePlan[]>;

    cellularDevice$: Observable<CellularDevice>;
    allFeatures$;
    enabledFeaturesList$: Observable<AdditionalFeature[]>;
    alreadyEnabled: boolean;
    featureObj: UIAdditionalFeature[] = [];
    originalFeatureObject: UIAdditionalFeature[] = [];
    subscriptions = [];
    userPermissions: string[];
    enabledIds = [];
    isSaveEnabled = false;
    isSaving = false;
    servicePlanDialogViewOptions = SERVICE_PLAN_DIALOG_VIEW;
    plans: ClareServicePlan[];
    planFeatures: ServicePlanFeature[] = [];
    availablePlans: ClareServicePlan[];
    availablePlanFeatures: ServicePlanFeature[];

    isMobile = true; //should only display the compare button if it's truly not mobile device
    isProjectOffline = false;

    private unsubscribe$: Subject<void> = new Subject<void>();

    constructor(
        private readonly servicePlanService: ServicePlansService,
        private readonly cellularService: CellularService,
        private readonly dialog: MatDialog,
        private readonly breakpointObserver$: BreakpointObserver,
        private readonly projectService: ProjectService,
        private readonly projectStatusService: ProjectStatusService,
        private readonly cdr: ChangeDetectorRef
    ) {}

    ngOnChanges(changes: NgChanges<ServicePlanOptionsComponent>): void {
        if (changes?.selectedPlan?.currentValue) {
            this.isSaveEnabled = this.didSelectionsChange();
        }
    }

    ngOnInit() {
        this.projectStatusService
            .getActiveProjectStatus$()
            .pipe(
                takeUntil(this.unsubscribe$),
                tap(status => {
                    this.isProjectOffline = status[0] === ClareStatuses.Offline;
                })
            )
            .subscribe();
        this.breakpointObserver$
            .observe(["(max-width: 959px)"])
            .pipe(
                map(mobileQuery => mobileQuery.matches),
                takeUntil(this.unsubscribe$)
            )
            .subscribe(isMobile => (this.isMobile = isMobile));
        this.cellularDevice$ = this.cellularService.fetchCellularDevice(this.cellularDevice?.iccid, this.cellularDevice?.provider);

        this.plans$ = this.servicePlanService.getActiveServicePlans(this.project.grandfathered);
        this.allFeatures$ = this.projectService.getAllProjectFeatures().pipe(
            map(features => Object.values(features)),
            takeUntil(this.unsubscribe$)
        );
        this.enabledFeaturesList$ = this.projectService.getAssignedProjectFeature();
        this.enabledFeaturesList$
            .pipe(
                map(features => {
                    features.forEach(feature => {
                        this.enabledIds.push(feature.id);
                        this.featureObj.push({ ...feature, is_enabled: true, enable_new_feature: null });
                        this.cdr.detectChanges();
                    });
                    this.originalFeatureObject = [...this.featureObj];
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe();

        this.isSaveEnabled = this.didSelectionsChange();

        this.plans$
            .pipe(
                take(1),
                tap(plans => {
                    this.setAvailablePlansAndFeatures(plans);
                })
            )
            .subscribe();
    }

    selectPlan(plan: ClareServicePlan, isFromComparePlans = false) {
        // only go back once
        if (isFromComparePlans && !this.isBackOnSelectIgnore) {
            this.goBack();
        }
        if (!this.isSelectedPlan(plan.id)) {
            this.selectedPlan = plan;
            this.isSaveEnabled = this.didSelectionsChange();
        }

        this.updateFieldChange();
    }

    hasSelectedFeature(feature: ServicePlanFeature) {
        return this.selectedPlan.features.some(selectedPlanFeature => selectedPlanFeature.featureKey === feature.featureKey);
    }

    isSelectedPlan(id: number) {
        return this.selectedPlan?.id === id;
    }

    getColor(plan) {
        return ServicePlanUtils.color(plan);
    }

    onSave(): void {
        this.isSaving = true;
        const emitter = new EventEmitter();

        this.servicePlanService.savePlans(
            this.subscription,
            this.currentPlan,
            this.selectedPlan,
            this.project,
            this.cellularDevice,
            emitter,
            this.featureObj,
            false,
            this.isProjectOffline
        );

        emitter
            .pipe(
                take(1),
                map(d => {
                    if (!!d && this.onUpdateDone) {
                        this.onUpdateDone.emit(d);
                    }
                    this.dialog.closeAll();
                    this.isSaving = false;
                })
            )
            .subscribe();
    }

    updateFeatureStatus(selectedFeature: AdditionalFeature, $event?: MatSlideToggleChange) {
        this.alreadyEnabled = this.isEnabled(selectedFeature);
        const newFeature = {
            ...selectedFeature,
            project_id: this.project.id,
            is_enabled: this.alreadyEnabled,
            enable_new_feature: $event?.checked,
        };
        this.featureObj.push(newFeature);
        const features = this.featureObj;
        this.featureObj = features.reduce((feature, cur) => {
            if (cur.is_enabled) {
                return [{ ...cur, project_id: this.project.id, enable_new_feature: $event?.checked ? null : false }];
            }
            return [{ ...cur, project_id: this.project.id, enable_new_feature: $event?.checked }];
        }, []) as UIAdditionalFeature[];

        this.isSaveEnabled = this.didSelectionsChange();
        this.updateFieldChange();
    }

    private updateFieldChange() {
        this.fieldsChange.emit({
            selectedPlan: this.selectedPlan,
            featureObj: this.featureObj || [],
        });
    }

    isEnabled(feature: AdditionalFeature) {
        return this.enabledIds.includes(feature?.id);
    }

    getFeatureDescription(description: string) {
        return description.replace(`<a`, `<a target="_blank"`);
    }

    goBack(): void {
        this.activeView = SERVICE_PLAN_DIALOG_VIEW.SELECT_PLAN;
        this.dialogContent.nativeElement.scrollTop = 0;
    }

    private didSelectionsChange(): boolean {
        let didChange = false;

        if (this.selectedPlan && (!this.currentPlan || this.currentPlan.id !== this.selectedPlan.id)) {
            didChange = true;
        }

        this.featureObj.forEach(feature => {
            const existingFeatureValue = this.originalFeatureObject.find(f => f.id === feature.id);
            if (
                (existingFeatureValue && existingFeatureValue.enable_new_feature !== feature.enable_new_feature) ||
                (!existingFeatureValue && feature.enable_new_feature === true)
            ) {
                didChange = true;
            }
        });

        return didChange;
    }

    private setAvailablePlansAndFeatures(plans: ClareServicePlan[]) {
        this.availablePlans = plans;
        this.availablePlanFeatures = this.servicePlanService.getAllPlanFeatures(plans).filter(p => p.description.length);
        this.cdr.detectChanges();
    }

    ngOnDestroy() {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }
}
